import { JsonObject } from '@bufbuild/protobuf';
import { parse, validate } from '@q6cyber/proto2es';
import { DirectCollect } from '@q6cyber/proto2es/generated/collect';
import moment from 'moment';

import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { FeatureFlagsService } from '../../../services';
import { SnackbarComponent } from '../../components';
import { TADirectTypeEnum } from '../../interfaces';
import { AirbrakeService } from '../airbrake/airbrake.service';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class UiHelpersService {
  readonly lineLimit = 100;
  readonly showLines = 50;
  readonly placeholderPath = '/assets/images/placeholder/placeholder.png';

  readonly deprecatedPermissions: string[] = ['CHECK_RECORDS'];

  snackBar = inject(MatSnackBar);
  private readonly _authService = inject(AuthService);
  private readonly _airbrakeService = inject(AirbrakeService);
  private readonly _location = inject(Location);
  featureFlagsService = inject(FeatureFlagsService);

  taDirectIcon(type: TADirectTypeEnum): string {
    switch (type) {
      case TADirectTypeEnum.CLIPBOARD:
        return 'assignment';
      case TADirectTypeEnum.MESSAGE:
        return 'sms';
      case TADirectTypeEnum.SCREENSHOT:
        return 'screenshot';
      case TADirectTypeEnum.KEYLOG:
        return 'vpn_key';
      case TADirectTypeEnum.FILE:
        return 'file_copy';
      case TADirectTypeEnum.HISTORY:
        return 'timeline';
      case TADirectTypeEnum.TA_ACCOUNT:
        return 'badge';
      case TADirectTypeEnum.CONVERSATION:
        return 'forum';
      case TADirectTypeEnum.WINDOW_LIST:
        return 'web_asset';
      default:
        return 'shuffle';
    }
  }

  formatProtoEnum<T>(array: T[]) {
    return array.splice(0, 1);
  }

  getToday(setTime = true) {
    const today = moment.utc();
    if (setTime) {
      today.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }
    return today.toISOString();
  }

  cleanUpPermissionsArray(
    permissions: string[],
    removeItem = 'AUTH_PERMISSION_UNKNOWN'
  ): string[] {
    const position = permissions.findIndex(
      permission => permission === removeItem
    );
    if (position !== -1) {
      permissions.splice(position, 1);
      return permissions;
    }
    return permissions;
  }

  showSnackbar(msg: string, ui: string[], duration = 7000): void {
    if (this.snackBar) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        data: {
          html: msg,
        },
        duration, //Pass 0 to keep it open indefinitely.
        panelClass: ui,
      });
    }
  }

  successSnackbar(msg: string, duration?: number): void {
    this.showSnackbar(msg, ['success-snackbar'], duration);
  }

  errorSnackbar(msg: string, duration?: number): void {
    this.showSnackbar(msg, ['error-snackbar'], duration);
  }

  warnSnackbar(msg: string, duration?: number): void {
    this.showSnackbar(msg, ['warn-snackbar'], duration);
  }

  informationSnackbar(msg: string, duration?: number): void {
    this.showSnackbar(msg, [], duration);
  }

  handleErrorMessage(error: any, message: string, duration?: number): void {
    if (error?.meta?.response?.status === 401) {
      void this._authService.logOut();
      return;
    }
    this.errorSnackbar(message, duration);

    // Trigger Airbrake
    if (error) {
      this._airbrakeService.notifyAirbrake(
        error,
        message ? { message } : undefined
      );
    }
  }

  truncateMessage(message: string) {
    if (message) {
      const lines = message.split('\n');
      const lineNum = lines.length;

      if (lineNum >= this.lineLimit) {
        const linesCut: string[] = [];
        for (let i = 0; i < this.showLines; i++) {
          linesCut.push(lines[i]);
        }

        return linesCut
          .join('\n')
          .concat('<strong class="secondary"> ... read more</strong>');
      } else {
        return message;
      }
    }
    return message;
  }

  formatResponse(item: any, nodeName = 'node'): any {
    if (item) {
      const keys = Object.keys(item);
      const node = item[nodeName];

      keys.forEach(key => {
        if (key !== nodeName) {
          node[key] = item[key];
        }
      });

      const validNode = this.validateAndParseProto<DirectCollect>(
        node,
        DirectCollect
      );

      node.corrupted = !validNode;

      if (
        this.featureFlagsService.isOn('experiment_dcv_highlight') &&
        item.highlight &&
        Object.keys(item.highlight).length > 0
      ) {
        if (
          item.highlight['message.text'] &&
          Array.isArray(item.highlight['message.text']) &&
          item.highlight['message.text'].length > 0 &&
          node.message &&
          typeof node.message.text !== 'undefined'
        ) {
          node.message.text = item.highlight['message.text'][0];
        }
      }

      return node;
    }
    return item;
  }

  formatBool(value: boolean) {
    const icon = value ? 'check' : 'close';
    const color = value ? 'text-green-600' : 'text-red-600';
    return `<span class="inline-flex justify-center w-full"><span class="material-icons-outlined ${color}">${icon}</span></span>`;
  }

  getBoolColor(value: boolean) {
    return value ? 'text-green-700' : 'text-gray-500';
  }

  getBoolIcon(value: boolean) {
    return value ? 'check_circle' : 'cancel';
  }

  mapping: { [key: string]: string } = {
    rdp: 'RDP',
    ato: 'ATO',
    ioc: 'IOC',
    vdf: 'VDF',
    ahp: 'AHP',
    aws: 'AWS',
    ce: 'CE',
    ttp: 'TTP',
    ta: 'TA',
    pan: 'PAN',
    dob: 'DOB',
  };

  mapToUppercase(value: string): string {
    const lowerCaseValue = value.toLowerCase();
    const words = lowerCaseValue.split(' ');

    const transformedWords = words.map(word => this.mapping[word] || word);

    const result = transformedWords.join(' ');

    return result.replace(/\b\w/g, char => char.toUpperCase());
  }

  toQueryString(params: { [key: string]: any }): string {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (Array.isArray(params[key])) {
        params[key].forEach((value: any) => {
          searchParams.append(key, value);
        });
      } else {
        searchParams.set(key, params[key]);
      }
    });
    return searchParams.toString();
  }

  // Inverse of toQueryString: repeated keys become an array. Null-prototype
  // object so a `__proto__` key can't pollute the prototype.
  fromQueryString(search: string): Record<string, string | string[]> {
    const params: Record<string, string | string[]> = Object.create(null);
    new URLSearchParams(search).forEach((value, key) => {
      const existing = params[key];
      if (existing === undefined) {
        params[key] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        params[key] = [existing, value];
      }
    });
    return params;
  }

  // Filters synced via Location.replaceState (see QueryParamsService) bypass
  // the Router, so ActivatedRoute.queryParams goes stale — read the real URL.
  getCurrentQueryParams(): Record<string, string | string[]> {
    const currentSearch = this._location.path().split('?')[1] ?? '';
    return this.fromQueryString(currentSearch);
  }

  cleanFilters<T extends object>(
    filters: T | null | undefined,
    bannedAttributes: string[] = []
  ): Partial<T> {
    if (!filters) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => {
        const isBanned = bannedAttributes.includes(key);
        const isEmpty =
          value == null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);

        return !isBanned && !isEmpty;
      })
    ) as Partial<T>;
  }

  checkScrollActive(tableView: HTMLElement | undefined): boolean {
    if (!tableView) {
      return false;
    } else {
      return tableView.scrollHeight > tableView.clientHeight;
    }
  }

  isEmptyObject(obj: {}): boolean {
    return Object.keys(obj).length === 0;
  }

  sortAlphabetically(array: string[]): string[] {
    return array.sort((a, b) => a.localeCompare(b));
  }

  getAmount(units: number, nanos: number): number {
    units = units === null || isNaN(units) ? 0 : units;
    nanos = nanos === null || isNaN(nanos) ? 0 : nanos;

    const totalNanos = nanos / 1000000000;
    return units + totalNanos;
  }

  getUnitsNanos(amount: number): { units: string; nanos: number } {
    let units = 0;
    let nanos = 0;

    if (amount) {
      units = Math.trunc(amount);
      nanos = Math.trunc(Number((amount - units).toFixed(9)) * 1000000000);
    }

    // Units as string due to Javascript Limitations on the implementation of int64 on proto.
    return { units: String(units), nanos };
  }

  isInputFocused(target: HTMLElement): boolean {
    if (!target) return false;

    const inputElements = ['INPUT', 'TEXTAREA', 'MAT-SELECT'];
    const contentEditableElements =
      target.getAttribute('contenteditable') === 'true';

    return inputElements.includes(target.tagName) || contentEditableElements;
  }

  navigateToElement(id: string): void {
    if (!id) return;

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  formatComments(value: { comment: string }[]) {
    if (!value || value.length === 0) {
      return '0 comments';
    }

    if (value.length === 1) {
      const firstComment = value[0].comment;
      const firstLine = firstComment.split('\n')[0];

      const truncated =
        firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine;
      return truncated || `${value.length} comments(s)`;
    }

    return `${value.length} comment(s)`;
  }

  validateAndParse(
    value: unknown,
    type: any,
    errorMessage?: string
  ): JsonObject | null {
    const validationError = validate(value, type, true);

    if (validationError) {
      if (errorMessage) {
        this.errorSnackbar(`${errorMessage}\n\nReason: ${validationError}`);
      }
      return null;
    }

    try {
      const parsed = parse(value, type, true);
      if (!parsed) {
        errorMessage &&
          this.errorSnackbar(
            `${errorMessage}\n\nReason: Failed to parse data.`
          );
        return null;
      }
      return parsed.toJson({ emitDefaultValues: true }) as JsonObject;
    } catch (e) {
      errorMessage && this.errorSnackbar(errorMessage);
      return null;
    }
  }

  validateAndParseProto<T>(
    value: unknown,
    type: any,
    errorMessage?: string
  ): T | null {
    const validationError = validate(value, type, true);

    if (validationError) {
      if (errorMessage) {
        this.errorSnackbar(`${errorMessage}\n\nReason: ${validationError}`);
      }
      return null;
    }

    try {
      const parsed = parse(value, type, true) as T;
      if (!parsed && errorMessage) {
        this.errorSnackbar(`${errorMessage}\n\nReason: Failed to parse data.`);
      }
      return parsed ?? null;
    } catch (e) {
      if (errorMessage) {
        this.errorSnackbar(errorMessage);
      }
      return null;
    }
  }

  isNonEmptyArray(...args: unknown[]): boolean {
    return args.length > 0 && args.every(e => Array.isArray(e) && e.length > 0);
  }
}
